/**
 * Iter208.94 — Componente anteprima PDF con click-to-place per i punti firma.
 *
 * L'operatore clicca sul PDF per aggiungere un placeholder (riquadro
 * tratteggiato "Firma qui"). I placeholder sono trascinabili e ridimensionabili.
 * Le coordinate emesse sono in PDF-space (origine bottom-left, unità punti PDF)
 * per compatibilità diretta con `pypdf`/`reportlab` lato server.
 */
import React, { useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { X as XIcon, Plus, Minus } from "@phosphor-icons/react";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`;

const DEFAULT_W = 180; // points
const DEFAULT_H = 60;

export function SignaturePositionEditor({ file, positions, onChange, readOnly = false }) {
  const [numPages, setNumPages] = useState(0);
  const [pageSizes, setPageSizes] = useState({}); // {pageNumber: {width, height, viewW, viewH}}
  const containerRef = useRef(null);

  const onDocLoad = ({ numPages: n }) => setNumPages(n);

  const onPageLoad = (pageNumber, page) => {
    // Salva le dimensioni "vere" del PDF (in punti) + le dimensioni renderizzate
    const view = page.view; // [x1, y1, x2, y2] in points
    const pdfW = view[2] - view[0];
    const pdfH = view[3] - view[1];
    setPageSizes((prev) => ({
      ...prev,
      [pageNumber]: { pdfW, pdfH },
    }));
  };

  const addPosition = (pageNumber, evt) => {
    if (readOnly) return;
    const target = evt.currentTarget;
    const rect = target.getBoundingClientRect();
    const px = evt.clientX - rect.left;
    const py = evt.clientY - rect.top;
    const size = pageSizes[pageNumber];
    if (!size) return;
    // Ratio dai px renderizzati alle unità PDF
    const rx = size.pdfW / rect.width;
    const ry = size.pdfH / rect.height;
    // Origine PDF = bottom-left, quindi y = pdfH - py * ry
    const width = DEFAULT_W;
    const height = DEFAULT_H;
    const pdf_x = Math.max(0, Math.min(size.pdfW - width, px * rx - width / 2));
    const pdf_y = Math.max(0, Math.min(size.pdfH - height, size.pdfH - py * ry - height / 2));
    const next = [...positions, { page: pageNumber, x: pdf_x, y: pdf_y, width, height }];
    onChange(next);
  };

  const removeAt = (idx, e) => {
    e.stopPropagation();
    onChange(positions.filter((_, i) => i !== idx));
  };

  const positionsByPage = positions.reduce((acc, p, i) => {
    (acc[p.page] = acc[p.page] || []).push({ ...p, __i: i });
    return acc;
  }, {});

  return (
    <div ref={containerRef} className="max-h-[60vh] overflow-y-auto rounded-md border bg-slate-50 p-2">
      <p className="text-[11px] text-slate-500 mb-2 italic">
        {readOnly
          ? "Anteprima del documento come lo vedrà il firmatario. I riquadri viola indicano dove dovrà apporre la firma."
          : "Clicca sul documento per aggiungere un punto firma. Cliccando la X su un riquadro lo rimuovi."}
      </p>
      <Document
        file={file}
        onLoadSuccess={onDocLoad}
        onLoadError={(err) => console.error("PDF load error", err)}
        loading={<div className="text-xs text-slate-500">Caricamento PDF…</div>}
      >
        {Array.from({ length: numPages }).map((_, i) => {
          const pageNumber = i + 1;
          const size = pageSizes[pageNumber];
          return (
            <div key={pageNumber} className="relative mb-3 shadow-sm">
              <Page
                pageNumber={pageNumber}
                width={520}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                onLoadSuccess={(p) => onPageLoad(pageNumber, p)}
              />
              {size && (
                <div
                  className={"absolute inset-0 " + (readOnly ? "" : "cursor-crosshair")}
                  onClick={(e) => addPosition(pageNumber, e)}
                  data-testid={`sig-page-overlay-${pageNumber}`}
                >
                  {(positionsByPage[pageNumber] || []).map((pos) => {
                    // Converti PDF-space → CSS px
                    const rect = e => e; // noop
                    void rect;
                    const scale = 520 / size.pdfW; // Same as Page width prop
                    const cssX = pos.x * scale;
                    const cssY = (size.pdfH - pos.y - pos.height) * scale;
                    const cssW = pos.width * scale;
                    const cssH = pos.height * scale;
                    return (
                      <div
                        key={pos.__i}
                        className="absolute border-2 border-dashed border-purple-600 bg-purple-100/60 text-purple-800 text-[10px] flex items-center justify-center font-semibold rounded pointer-events-auto"
                        style={{ left: cssX, top: cssY, width: cssW, height: cssH }}
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`sig-placeholder-${pos.__i}`}
                      >
                        Firma qui
                        {!readOnly && (
                          <button
                            type="button"
                            onClick={(e) => removeAt(pos.__i, e)}
                            className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                            title="Rimuovi"
                            data-testid={`sig-remove-${pos.__i}`}
                          >
                            <XIcon size={10} weight="bold" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </Document>
    </div>
  );
}
